// Billing & Subscription -- flat table with every Active site + all
// their subscribed modules' due dates + amounts. Amber cell when a
// module's renewal date is within 30 days; red if overdue. Drives
// Dave's weekly "who do I need to invoice" workflow. Replaces the
// external xsiteio-side billing view.
//
// Smart columns: only modules that at least one site subscribes to
// get a column. Keeps the table narrower on a book that doesn't use
// every module.
//
// Dave 2026-07-09.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const NEAR_DAYS = 30;

function fmtMoney(v) {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// Both formatters do MANUAL string assembly instead of toLocaleDateString
// so the output is deterministic and doesn't depend on the browser's
// system locale (Dave 2026-07-09 was getting weekday-prefixed dates on
// his machine). US month-day-year order.
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtShortDate(iso) {
  if (!iso) return '';
  // ISO YYYY-MM-DD -> MM/DD/YY (compact, mono-friendly).
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  if (!y || !m || !d) return iso;
  return `${m}/${d}/${String(y).slice(2)}`;
}
function fmtLongDate(iso) {
  if (!iso) return '';
  // ISO YYYY-MM-DD -> "Apr 02, 2027" (US month-day-year).
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  if (!y || !m || !d) return iso;
  const mi = Number(m) - 1;
  const monthLabel = MONTHS_SHORT[mi] || m;
  return `${monthLabel} ${d}, ${y}`;
}

function daysUntil(iso) {
  if (!iso) return null;
  const target = new Date(String(iso).slice(0, 10) + 'T00:00:00').getTime();
  if (Number.isNaN(target)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today.getTime()) / (1000 * 60 * 60 * 24));
}
// 'red' = overdue, 'amber' = <= 30 days, 'green' = > 30 days, null = no data
function urgencyBucket(iso) {
  if (!iso) return null;
  const d = daysUntil(iso);
  if (d == null) return null;
  if (d < 0)             return 'red';
  if (d <= NEAR_DAYS)    return 'amber';
  return 'green';
}
const BUCKET_STYLE = {
  red:   { bg: '#fee2e2', fg: '#b91c1c' },
  amber: { bg: '#fef3c7', fg: '#b45309' },
  green: { bg: '#ecfdf5', fg: '#047857' },  // very soft mint so healthy rows aren't loud
};

// Compact "due date on top, amount below" cell.
function ModuleCell({ mod }) {
  if (!mod || (!mod.due && (mod.annual == null || mod.annual === 0))) {
    return <div style={{ color: 'var(--color-text-faint)' }}>-</div>;
  }
  const bucket = urgencyBucket(mod.due);
  const style = bucket ? BUCKET_STYLE[bucket] : { bg: 'transparent', fg: 'var(--color-text)' };
  return (
    <div style={{
      background: style.bg, color: style.fg,
      padding: '3px 6px', borderRadius: 3,
      fontSize: 11, fontFamily: 'var(--font-mono)',
      lineHeight: 1.2, textAlign: 'right',
      minWidth: 74, whiteSpace: 'nowrap',
    }}>
      <div style={{ fontWeight: 700 }}>{fmtShortDate(mod.due)}</div>
      <div>{mod.annual != null ? `$${fmtMoney(mod.annual)}` : ''}</div>
    </div>
  );
}

function exportCsv(rows, activeModules) {
  const headers = [
    'Company', 'Site Name', 'City', 'Prov',
    'Phone Main', 'Phone Cell', 'Email',
    'xsiteio Client ID', 'xsiteio Site ID', 'xsiteio Username',
    'Customer Since', 'Last Updated (xsiteio)', 'Last Login (xsiteio)',
  ];
  for (const m of activeModules) {
    headers.push(`${m.label} Due`);
    headers.push(`${m.label} Amount`);
  }
  const csvRows = [headers.join(',')];
  for (const s of rows) {
    const row = [
      s.company, s.site_name, s.city, s.province_state,
      s.phone_main, s.phone_cell, s.email,
      s.xsiteio_client_id, s.xsiteio_site_id, s.xsite_io_username,
      s.install_date ? String(s.install_date).slice(0, 10) : '',
      s.xsiteio_last_updated ? String(s.xsiteio_last_updated).slice(0, 10) : '',
      s.xsiteio_last_login ? String(s.xsiteio_last_login).slice(0, 10) : '',
    ];
    for (const m of activeModules) {
      const mod = s.modules?.[m.key] || {};
      row.push(mod.due || '');
      row.push(mod.annual != null ? mod.annual : '');
    }
    csvRows.push(row.map((v) => {
      if (v == null) return '';
      const s = String(v);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
      return s;
    }).join(','));
  }
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `billing-subscription-${stamp}.csv`;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function BillingSubscription() {
  const [data, setData] = useState({ sites: [], active_modules: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  // Per-module filter (bucket: 'all' | 'red' | 'amber' | 'green' | 'blank')
  const [modFilters, setModFilters] = useState({});
  const [sortKey, setSortKey] = useState('earliest_due');
  const [sortDir, setSortDir] = useState('asc');
  // Row-level earliest-due filter, driven by the clickable summary chips.
  // 'all' | 'red' | 'amber' | 'green' | 'blank'
  const [dueFilter, setDueFilter] = useState('all');

  function load() {
    setLoading(true);
    setError(null);
    axios.get('/api/billing-subscription')
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  const activeModules = data.active_modules || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.sites || []).filter((s) => {
      if (q) {
        const hay = [
          s.company, s.site_name, s.site_number, s.city, s.province_state,
          s.pos_type, s.email, s.phone_main, s.phone_cell,
          s.xsiteio_client_id, s.xsite_io_username,
        ].map((v) => v == null ? '' : String(v).toLowerCase()).join(' | ');
        if (!hay.includes(q)) return false;
      }
      // Per-module bucket filters. A site passes when EVERY set filter matches.
      for (const [modKey, wanted] of Object.entries(modFilters)) {
        if (!wanted || wanted === 'all') continue;
        const mod = s.modules?.[modKey];
        const bucket = mod?.due ? urgencyBucket(mod.due) : 'blank';
        if (wanted === 'blank' && bucket !== 'blank') return false;
        if (wanted !== 'blank' && bucket !== wanted)  return false;
      }
      return true;
    });
  }, [data.sites, search, modFilters]);

  // Row-level bucket filter applied on top of `filtered`. Kept separate
  // so the summary chips (computed from `filtered`) always show the full
  // breakdown even while one bucket is selected -- you can switch chips.
  const dueFiltered = useMemo(() => {
    if (dueFilter === 'all') return filtered;
    return filtered.filter((s) => {
      const b = s.earliest_due ? urgencyBucket(s.earliest_due) : 'blank';
      return b === dueFilter;
    });
  }, [filtered, dueFilter]);

  const sorted = useMemo(() => {
    const arr = [...dueFiltered];
    arr.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [dueFiltered, sortKey, sortDir]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }
  function sortArrow(key) {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' up' : ' down';
  }

  // Summary counts across the filtered set for the top-of-page chips.
  const summary = useMemo(() => {
    const c = { red: 0, amber: 0, green: 0, blank: 0 };
    for (const s of filtered) {
      // Bucket by earliest module due date (matches Dave's "when does
      // ANYTHING come due for this site" mental model).
      const b = s.earliest_due ? urgencyBucket(s.earliest_due) : 'blank';
      c[b]++;
    }
    return c;
  }, [filtered]);

  // Clickable summary chips. Each toggles the row-level `dueFilter`.
  const CHIPS = [
    { key: 'red',   label: 'Overdue',             bg: '#fee2e2', fg: '#b91c1c', count: summary.red },
    { key: 'amber', label: 'Due ≤30d',            bg: '#fef3c7', fg: '#b45309', count: summary.amber },
    { key: 'green', label: 'OK',                  bg: '#ecfdf5', fg: '#047857', count: summary.green },
    { key: 'blank', label: 'No renewal on file',  bg: '#f3f4f6', fg: '#6b7280', count: summary.blank },
  ];

  if (loading) return <section className="page"><h2>Billing & Subscription</h2><p>Loading...</p></section>;
  if (error)   return <section className="page"><h2>Billing & Subscription</h2><p style={{ color: 'var(--color-bad)' }}>Error: {error}</p></section>;

  return (
    <section className="page">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0 }}>Billing & Subscription</h2>
          <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 4 }}>
            {dueFiltered.length} site{dueFiltered.length === 1 ? '' : 's'} shown{dueFilter !== 'all' ? ' (filtered)' : ''} -- click a row to jump to the customer
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button className="btn-secondary" onClick={load} title="Reload from server">Refresh</button>
          <button className="btn-secondary" onClick={() => exportCsv(sorted, activeModules)} title="Download all rows as CSV">Export CSV</button>
        </div>
      </div>

      {/* Summary strip + urgency chips */}
      <div style={{
        display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap',
        marginBottom: 'var(--space-3)',
      }}>
        {CHIPS.map((chip) => {
          const active = dueFilter === chip.key;
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => setDueFilter(active ? 'all' : chip.key)}
              title={active ? 'Click to clear this filter' : `Show only: ${chip.label}`}
              style={{
                background: chip.bg, color: chip.fg,
                padding: '4px 10px', borderRadius: 4, fontWeight: 700,
                fontFamily: 'inherit', fontSize: 'inherit',
                cursor: 'pointer',
                border: active ? `2px solid ${chip.fg}` : '2px solid transparent',
                boxShadow: active ? `0 0 0 2px ${chip.bg}, 0 0 0 3px ${chip.fg}` : 'none',
                opacity: dueFilter === 'all' || active ? 1 : 0.55,
                transition: 'opacity 120ms, box-shadow 120ms',
              }}
            >
              {chip.label}: {chip.count}
            </button>
          );
        })}
        {dueFilter !== 'all' && (
          <button
            type="button"
            onClick={() => setDueFilter('all')}
            title="Clear the urgency filter"
            style={{
              background: 'transparent', color: 'var(--color-text-dim)',
              padding: '4px 8px', borderRadius: 4, border: 'none',
              cursor: 'pointer', fontFamily: 'inherit', fontSize: 12,
              textDecoration: 'underline',
            }}
          >
            Clear filter
          </button>
        )}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 'var(--space-3)' }}>
        <input
          type="search"
          placeholder="Search company, site #, POS, email, xsiteio username..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: '100%', maxWidth: 500, padding: '6px 10px' }}
        />
      </div>

      {/* Scrollable table */}
      <div style={{ overflow: 'auto', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: 'var(--color-surface-sunken, #f8fafc)', position: 'sticky', top: 0, zIndex: 1 }}>
            <tr>
              <th style={{ padding: '6px 8px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('site_name')}>Site{sortArrow('site_name')}</th>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Phone</th>
              <th style={{ padding: '6px 8px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('earliest_due')}>Next Due{sortArrow('earliest_due')}</th>
              {activeModules.map((m) => (
                <th key={m.key} style={{ padding: '6px 8px', textAlign: 'center', minWidth: 90 }}>
                  <div style={{ fontWeight: 700 }}>{m.label}</div>
                  <select
                    value={modFilters[m.key] || 'all'}
                    onChange={(e) => setModFilters((prev) => ({ ...prev, [m.key]: e.target.value }))}
                    style={{ marginTop: 4, fontSize: 10, padding: '2px 4px', border: '1px solid var(--color-border)', borderRadius: 3 }}
                    title={`Filter ${m.label}`}
                  >
                    <option value="all">All</option>
                    <option value="red">Overdue</option>
                    <option value="amber">Due ≤30d</option>
                    <option value="green">OK</option>
                    <option value="blank">Not subscribed</option>
                  </select>
                </th>
              ))}
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Last Updated</th>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Last Login</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => (
              <tr key={s.site_id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '5px 8px' }}>
                  {/* Site name links to the parent customer detail page
                      with the site_number query param so CustomerDetail
                      auto-expands that specific site's row on arrival. */}
                  <Link
                    to={`/customers/${s.customer_id}${s.site_number ? `?site_number=${encodeURIComponent(s.site_number)}` : ''}`}
                    title={s.company || ''}
                  >
                    {s.site_name || s.company || '-'}
                  </Link>
                </td>
                <td style={{ padding: '5px 8px', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                  {s.phone_cell || s.phone_main || '-'}
                </td>
                <td style={{ padding: '5px 8px', fontFamily: 'var(--font-mono)', fontSize: 11, whiteSpace: 'nowrap', minWidth: 110 }}>
                  {s.earliest_due ? (
                    <span style={(() => {
                      const b = urgencyBucket(s.earliest_due);
                      const st = b ? BUCKET_STYLE[b] : null;
                      return st ? { padding: '2px 6px', borderRadius: 3, background: st.bg, color: st.fg, fontWeight: 700 } : {};
                    })()}>
                      {fmtLongDate(s.earliest_due)}
                    </span>
                  ) : '-'}
                </td>
                {activeModules.map((m) => (
                  <td key={m.key} style={{ padding: '4px 6px' }}>
                    <ModuleCell mod={s.modules?.[m.key]} />
                  </td>
                ))}
                <td style={{ padding: '5px 8px', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-text-dim)' }}>
                  {s.xsiteio_last_updated ? fmtShortDate(s.xsiteio_last_updated) : '-'}
                </td>
                <td style={{ padding: '5px 8px', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-text-dim)' }}>
                  {s.xsiteio_last_login ? fmtShortDate(s.xsiteio_last_login) : '-'}
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={4 + activeModules.length} style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-dim)' }}>
                  No sites match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
